import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningIcon from '@mui/icons-material/Warning';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAIFileUploadV2Context } from 'src/contexts/AIFileUploadV2Context';
import { getStepProgressLabel } from 'src/components/NewRenterInterface/AIFileUploadV2/utils/stepLabels';
import { ErrorBanners } from 'src/components/NewRenterInterface/AIFileUploadV2/components/ErrorBanners';
import { ContactInfoBanner } from 'src/components/NewRenterInterface/AIFileUploadV2/components/ContactInfoBanner';
import {
  errorCopyLayout,
  renderErrorCopy,
} from 'src/components/NewRenterInterface/AIFileUploadV2/utils/renderErrorCopy';
import { useMergedFailedFiles } from 'src/hooks/aiFileUploadV2/useMergedFailedFiles';
import {
  reuseCrossMemberFile,
} from 'src/services/aiFileUploadV2';
import type { ExistingSurveyFile } from 'src/redux/slices/Interview/types';
import type {
  CrossMemberDocument,
  ReusedSurveyFile,
} from 'src/types/aiFileUploadV2';

interface Props {
  onContinue: () => void;
  onFileDetail: (fileId: number) => void;
  onHasUploadsChange?: (count: number) => void;
  onReadyStateChange?: (state: { processing: boolean; ready: boolean }) => void;
  maxFiles?: number;
  questionGroupId?: number;
  onAnswerGroupCreated?: (groupId: number) => void;
  existingSurveyFiles?: ExistingSurveyFile[];
  onSurveyFileDetail?: (file: ExistingSurveyFile) => void;
  onSurveyFileDelete?: (file: ExistingSurveyFile) => void;
  /**
   * Reports the documents the backend linked onto this question instead of
   * creating a row (ENG-3954/ENG-4064). The parent owns the merged file list
   * so the durable answer saved on Continue includes them.
   */
  onSurveyFilesLinked?: (files: ExistingSurveyFile[]) => void;
  /**
   * Whether the acting user is allowed to upload files for this survey.
   * Driven by `can_answer` from the IPE response — false when the user
   * lacks proxy authority (e.g. not a managing member of this cert).
   * Defaults to true so that self-mode interviews are unaffected.
   * See ENG-3002.
   */
  canUpload?: boolean;
}

// TERMINAL/REVIEWABLE deliberately do NOT include needs_member_confirmation.
// Unlike needs_classification (which has a dedicated funnel that resolves it
// before ready:true can fire), there is no equivalent gating path for a
// cross-member mismatch here — the renter resolves it inline via FileDetail
// (see renderTile below), but until they do, this upload step must not
// report itself "ready" and let the interview move on past an unresolved
// pause. See docs/plans/sorting-hat-cross-member-and-unification.md §1.
const TERMINAL = new Set([
  'completed',
  'failed',
  'rejected',
  'cancelled',
  'needs_classification',
]);
const REVIEWABLE = new Set(['completed', 'needs_classification']);

function formatFilenames(names: string[]): string {
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names[0]} and ${names.length - 1} more files`;
}

/**
 * Map a document the backend linked onto this question into the tile shape the
 * interview file list renders (ENG-4064). The SurveyFile identity and preview
 * URL come straight from the upload response, so the tile can be opened and
 * deleted without the RenterFile bridge (which expects a RenterFile id).
 */
function toLinkedSurveyFile(item: ReusedSurveyFile): ExistingSurveyFile {
  return {
    file_id: item.survey_file_id,
    file_name: item.original_filename || 'Document',
    pdf_file_url: item.pdf_file_url || '',
    survey_answer_id: item.survey_answer_id ?? 0,
    // SurveyFile.Status.BLUE: answered, not yet reviewed. Renders as an
    // editable tile with the same delete affordance as a stored file.
    status: item.survey_file_status ?? 4,
    is_rejected: false,
    created_at: item.survey_file_created_at ?? new Date().toISOString(),
  };
}

export default function FileUploadInput({
  onContinue: _onContinue,
  onFileDetail,
  onHasUploadsChange,
  onReadyStateChange,
  maxFiles = 6,
  questionGroupId,
  onAnswerGroupCreated,
  existingSurveyFiles = [],
  onSurveyFileDetail,
  onSurveyFileDelete,
  onSurveyFilesLinked,
  canUpload = true,
}: Props): React.ReactElement {
  const {
    files: { fileMap, fileIds, uploadFiles, removeFile, failedEntries },
    surveyId,
    targetQitId,
    targetAnswerGroupId,
    setTargetAnswerGroupId,
    propertyContact,
  } = useAIFileUploadV2Context();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const sessionFileIdsRef = useRef<Set<number>>(new Set());
  const [blobUrls, setBlobUrls] = useState<Record<number, string>>({});
  // ENG-4064: pre-flight outcomes that create no upload row (linked /
  // already on file / processing / needs your input). The renter saw nothing
  // for these before, so they re-dropped the file and got blocked.
  const [linkedNotice, setLinkedNotice] = useState<string[]>([]);
  const [alreadyOnFileNotice, setAlreadyOnFileNotice] = useState<string[]>([]);
  const [processingNotice, setProcessingNotice] = useState<string[]>([]);
  const [existingNotice, setExistingNotice] = useState<string[]>([]);
  // ENG-4086: the copy lives on another member's survey of this cert. No row
  // was created; the renter either reuses it explicitly or leaves it.
  const [crossMemberNotice, setCrossMemberNotice] = useState<
    CrossMemberDocument[]
  >([]);
  const [reusingId, setReusingId] = useState<number | null>(null);
  const [reuseErrors, setReuseErrors] = useState<number[]>([]);
  const [highlightedSurveyFileId, setHighlightedSurveyFileId] = useState<
    number | null
  >(null);

  const clearReuseNotices = (): void => {
    setLinkedNotice([]);
    setAlreadyOnFileNotice([]);
    setProcessingNotice([]);
    setExistingNotice([]);
    setCrossMemberNotice([]);
    setReuseErrors([]);
    setHighlightedSurveyFileId(null);
  };

  /**
   * ENG-4086: explicit reuse of a document already on another member's survey.
   * The server is idempotent, so a double tap returns the same SurveyFile. The
   * returned file goes through the same linked path as ENG-3954, so the tile
   * fills exactly like an automatic link.
   */
  const handleReuseCrossMember = useCallback(async (
    sourceSurveyFileId: number,
    targetQitId: number,
    targetAnswerGroupId?: number | null,
  ): Promise<void> => {
    if (!surveyId) {
      return;
    }
    setReuseErrors((prev) => prev.filter((id) => id !== sourceSurveyFileId));
    setReusingId(sourceSurveyFileId);
    try {
      const reused = await reuseCrossMemberFile({
        surveyId,
        sourceSurveyFileId,
        targetQitId,
        ...(targetAnswerGroupId ? { targetAnswerGroupId } : {}),
      });
      const linked = toLinkedSurveyFile(reused);
      onSurveyFilesLinked?.([linked]);
      setLinkedNotice([linked.file_name]);
      setCrossMemberNotice((prev) =>
        prev.filter(
          (doc) => doc.source_survey_file_id !== sourceSurveyFileId
        )
      );
    } catch {
      setReuseErrors((prev) => [...prev, sourceSurveyFileId]);
    } finally {
      setReusingId(null);
    }
  }, [surveyId, onSurveyFilesLinked]);

  const handleBannerReuse = useCallback(
    ({
      sourceSurveyFileId,
      targetQitId,
    }: {
      sourceSurveyFileId: number;
      targetQitId: number;
    }): Promise<void> =>
      handleReuseCrossMember(
        sourceSurveyFileId,
        targetQitId,
        targetAnswerGroupId
      ),
    [handleReuseCrossMember, targetAnswerGroupId]
  );

  const bannerFiles = useMergedFailedFiles(
    { survey: surveyId, qit: targetQitId },
    failedEntries
  );

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = Array.from(e.target.files ?? []).slice(
      0,
      maxFiles - fileIds.length
    );
    if (!files.length || !surveyId) {
      return;
    }
    clearReuseNotices();
    const blobsTemp = files.map((f) => URL.createObjectURL(f));
    const result = await uploadFiles(surveyId, files, {
      targetQitId: targetQitId ?? undefined,
      targetAnswerGroupId,
      questionGroupId,
    });
    if (result.createdAnswerGroupId) {
      setTargetAnswerGroupId(result.createdAnswerGroupId);
      onAnswerGroupCreated?.(result.createdAnswerGroupId);
    }
    // ENG-4064: surface every non-created outcome. `linked` also becomes a
    // real tile in the question list (the parent owns the merged list).
    if (result.linked.length > 0) {
      const linkedFiles = result.linked.map(toLinkedSurveyFile);
      setLinkedNotice(linkedFiles.map((file) => file.file_name));
      onSurveyFilesLinked?.(linkedFiles);
    }
    if (result.alreadyOnFile.length > 0) {
      setAlreadyOnFileNotice(
        result.alreadyOnFile.map((doc) => doc.filename ?? 'This document')
      );
      const matched = result.alreadyOnFile.find(
        (doc) => typeof doc.survey_file_id === 'number'
      );
      setHighlightedSurveyFileId(matched?.survey_file_id ?? null);
    }
    if (result.processing.length > 0) {
      setProcessingNotice(
        result.processing.map((file) => file.original_filename)
      );
    }
    if (result.existing.length > 0) {
      setExistingNotice(result.existing.map((file) => file.original_filename));
    }
    if (result.crossMember && result.crossMember.length > 0) {
      setCrossMemberNotice(result.crossMember);
    }
    result.fileIds.forEach((id, i) => {
      sessionFileIdsRef.current.add(id);
      setBlobUrls((prev) => ({ ...prev, [id]: blobsTemp[i] }));
    });
    // revoke orphaned blobs (uploadFiles returned fewer IDs than files, e.g. on error)
    blobsTemp
      .slice(result.fileIds.length)
      .forEach((url) => URL.revokeObjectURL(url));
    e.target.value = '';
  };

  const handleRemove = (id: number): void => {
    const url = blobUrls[id];
    if (url) {
      URL.revokeObjectURL(url);
    }
    setBlobUrls((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
    sessionFileIdsRef.current.delete(id);
    void removeFile(id);
  };

  const triggerFilePicker = (): void => {
    fileInputRef.current?.click();
  };

  const completedCount = fileIds.filter((id) => {
    const status = fileMap[id]?.status;
    return (
      status === 'completed' ||
      status === 'needs_classification' ||
      status === 'needs_member_confirmation'
    );
  }).length;

  // "Uploaded but not accepted" = RenterFiles still in the active session
  // (session_status === 'active'). 'applied' files are already accepted and
  // must not drive the Continue button.
  const activeFileIds = fileIds.filter(
    (id) => fileMap[id]?.session_status === 'active'
  );
  const hasActive = activeFileIds.length > 0;

  useEffect(() => {
    onHasUploadsChange?.(fileIds.length + existingSurveyFiles.length);
  }, [fileIds.length, existingSurveyFiles.length, onHasUploadsChange]);

  const allTerminal =
    hasActive && activeFileIds.every((id) => TERMINAL.has(fileMap[id]?.status));
  const anyReviewable = activeFileIds.some((id) =>
    REVIEWABLE.has(fileMap[id]?.status)
  );

  // ENG-4064: a linked/already-on-file outcome creates no session row, so the
  // question can be answerable purely from the files already on it. Readiness
  // is therefore "there is a file here, nothing is still processing, and no
  // session file ended in a non-reviewable terminal state".
  const hasAnyFile = fileIds.length + existingSurveyFiles.length > 0;
  const isProcessing = hasActive && !allTerminal;
  const blockedBySessionStatus = hasActive && allTerminal && !anyReviewable;

  useEffect(() => {
    onReadyStateChange?.({
      processing: isProcessing,
      ready: hasAnyFile && !isProcessing && !blockedBySessionStatus,
    });
  }, [hasAnyFile, isProcessing, blockedBySessionStatus, onReadyStateChange]);

  const removeButton = (id: number): React.ReactElement => (
    <IconButton
      size="small"
      data-testid={`remove-file-${id}`}
      onClick={(e) => {
        e.stopPropagation();
        handleRemove(id);
      }}
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        '&:hover': { bgcolor: 'grey.100' },
      }}
    >
      <CloseIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
    </IconButton>
  );

  const tileBase = {
    bgcolor: 'background.paper',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: 'grey.300',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    px: 2,
    py: 1.5,
    overflow: 'hidden',
  };

  const filenameSx = {
    flex: 1,
    fontSize: 13,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const tileLine1Sx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minWidth: 0,
  };

  const tileLine2Sx = {
    // Indent past remove-btn (24px) + gap (12px) + icon (20px) + gap (12px) = 68px
    pl: '68px',
    mt: '-2px',
    minWidth: 0,
  };

  const renderTile = (index: number): React.ReactElement => {
    const id = fileIds[index];

    if (id === undefined) {
      // Empty placeholder
      return (
        <Box
          key={`empty-${index}`}
          onClick={triggerFilePicker}
          sx={{
            ...tileBase,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: 'background.neutral',
            cursor: 'pointer',
            '&:hover': { borderColor: 'grey.400' },
          }}
        >
          <AddIcon sx={{ fontSize: 20, color: 'grey.400' }} />
          <Typography sx={{ flex: 1, fontSize: 13, color: 'text.disabled' }}>
            File {index + 1}
          </Typography>
        </Box>
      );
    }

    const file = fileMap[id];
    const status = file?.status;
    const isSessionFile = sessionFileIdsRef.current.has(id);

    if (status === 'pending') {
      return (
        <Box
          key={`tile-${id}`}
          sx={{
            ...tileBase,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {removeButton(id)}
          <CircularProgress size={18} />
          <Typography
            sx={{
              flex: 1,
              fontSize: 13,
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            Uploading...
          </Typography>
        </Box>
      );
    }

    if (
      status === 'processing' ||
      status === 'classified' ||
      status === 'extracting'
    ) {
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <InsertDriveFileIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {getStepProgressLabel(file)}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (status === 'completed') {
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <InsertDriveFileIcon sx={{ fontSize: 20, color: 'success.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'success.main' }}>
              Processed
            </Typography>
          </Box>
        </Box>
      );
    }

    if (status === 'needs_classification') {
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'warning.main' }}>
              Label needed
            </Typography>
          </Box>
        </Box>
      );
    }

    // Renter-interview cross-member mismatch pause (verify mode) — see
    // docs/plans/sorting-hat-cross-member-and-unification.md §1. Tapping
    // opens FileDetail, which renders the three-way confirm/reject/delete
    // widget for this status.
    if (status === 'needs_member_confirmation') {
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'warning.main' }}>
              Confirmation needed
            </Typography>
          </Box>
        </Box>
      );
    }

    if (status === 'failed') {
      const errorCaption =
        errorCopyLayout(file).headline || 'Processing failed';
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'error.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <InsertDriveFileIcon sx={{ fontSize: 20, color: 'error.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'error.main' }}>
              {renderErrorCopy(errorCaption)}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (status === 'rejected') {
      const rejectedCaption = file.rejection_reason_category ?? 'File rejected';
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'error.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <InsertDriveFileIcon sx={{ fontSize: 20, color: 'error.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'error.main' }}>
              {renderErrorCopy(errorCopyLayout(file).body || rejectedCaption)}
            </Typography>
          </Box>
        </Box>
      );
    }

    // Historical file (not in session) or unknown status — show as completed-style with "Tap to view"
    if (!isSessionFile && file) {
      return (
        <Box
          key={`tile-${id}`}
          onClick={() => onFileDetail(id)}
          sx={{
            ...tileBase,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.light' },
          }}
        >
          <Box sx={tileLine1Sx}>
            {removeButton(id)}
            <InsertDriveFileIcon sx={{ fontSize: 20, color: 'success.main' }} />
            <Typography sx={filenameSx}>{file.original_filename}</Typography>
            <ChevronRightIcon
              sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
            />
          </Box>
          <Box sx={tileLine2Sx}>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
              Tap to view
            </Typography>
          </Box>
        </Box>
      );
    }

    // Fallback empty placeholder
    return (
      <Box
        key={`empty-${index}`}
        onClick={triggerFilePicker}
        sx={{
          ...tileBase,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.neutral',
          cursor: 'pointer',
          '&:hover': { borderColor: 'grey.400' },
        }}
      >
        <AddIcon sx={{ fontSize: 20, color: 'grey.400' }} />
        <Typography sx={{ flex: 1, fontSize: 13, color: 'text.disabled' }}>
          File {index + 1}
        </Typography>
      </Box>
    );
  };

  return (
    <Box data-testid="file-upload-input">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        data-testid="file-input"
      />

      {/* Contact info banner — shown when any file fails during this session;
          same behavior as the standalone upload page (ENG-2838) */}
      {Object.keys(failedEntries).length > 0 && propertyContact && (
        <Box sx={{ mb: 1 }}>
          <ContactInfoBanner
            contact={propertyContact}
            key={Object.keys(failedEntries).length}
          />
        </Box>
      )}

      {/* Removal-reason banners — files auto-deleted by the pipeline land in
          failedEntries; merged with persisted failed files (ENG-3265) */}
      {bannerFiles.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <ErrorBanners
            files={bannerFiles}
            onReuseCrossMember={handleBannerReuse}
          />
        </Box>
      )}

      {fileIds.length + existingSurveyFiles.length < maxFiles && canUpload && (
        <Box
          onClick={triggerFilePicker}
          data-testid="upload-zone"
          sx={{
            height: 100,
            bgcolor: 'background.paper',
            borderRadius: '14px',
            border: '2px dashed',
            borderColor: 'primary.lighter',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            cursor: 'pointer',
            mb: 2,
            '&:hover': { borderColor: 'primary.light' },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography
            sx={{ fontSize: 16, fontWeight: 600, color: 'primary.main' }}
          >
            Tap to upload
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
            PDF, JPG, PNG, HEIC · Max 50MB
          </Typography>
        </Box>
      )}

      {/* ENG-4064: pre-flight outcomes that create no upload row. The renter
          used to see nothing and re-drop the file, which then blocked. */}
      {linkedNotice.length > 0 ? (
        <Alert
          severity="success"
          onClose={() => setLinkedNotice([])}
          sx={{ mb: 1.5 }}
          data-testid="linked-notice"
        >
          <span>
            {formatFilenames(linkedNotice)} already on file. We added{' '}
            {linkedNotice.length === 1 ? 'it' : 'them'} to this question.
          </span>
        </Alert>
      ) : null}

      {alreadyOnFileNotice.length > 0 ? (
        <Alert
          severity="success"
          onClose={() => setAlreadyOnFileNotice([])}
          sx={{ mb: 1.5 }}
          data-testid="already-on-file-notice"
        >
          <span>
            {formatFilenames(alreadyOnFileNotice)} already on this question.
          </span>
        </Alert>
      ) : null}

      {processingNotice.length > 0 ? (
        <Alert
          severity="info"
          onClose={() => setProcessingNotice([])}
          sx={{ mb: 1.5 }}
          data-testid="processing-notice"
        >
          <span>
            {formatFilenames(processingNotice)}{' '}
            {processingNotice.length === 1 ? 'is' : 'are'} already being
            checked. Give it a moment.
          </span>
        </Alert>
      ) : null}

      {crossMemberNotice.length > 0 ? (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          data-testid="cross-member-notice"
        >
          {crossMemberNotice.map((doc) => {
            const failed = reuseErrors.includes(doc.source_survey_file_id);
            const busy = reusingId === doc.source_survey_file_id;
            return (
              <Box
                key={doc.source_survey_file_id}
                sx={{ mb: crossMemberNotice.length > 1 ? 1.5 : 0 }}
              >
                <Typography component="span" sx={{ fontSize: 13 }}>
                  This document is already on{' '}
                  <strong>
                    {doc.member_name ?? 'another household member'}
                  </strong>
                  &apos;s answer to{' '}
                  <strong>{doc.question ?? 'a question'}</strong>.
                </Typography>
                <Box
                  sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={busy}
                    onClick={() =>
                      handleReuseCrossMember(
                        doc.source_survey_file_id,
                        doc.target_qit_id,
                        doc.target_answer_group_id
                      )
                    }
                    data-testid={`use-for-me-too-${doc.source_survey_file_id}`}
                  >
                    {busy ? 'Adding\u2026' : 'Use it for me too'}
                  </Button>
                  {failed ? (
                    <Typography sx={{ fontSize: 12, color: 'error.main' }}>
                      We could not add it here. Try uploading it again.
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Alert>
      ) : null}

      {existingNotice.length > 0 ? (
        <Alert
          severity="info"
          onClose={() => setExistingNotice([])}
          sx={{ mb: 1.5 }}
          data-testid="existing-notice"
        >
          <span>
            {formatFilenames(existingNotice)}{' '}
            {existingNotice.length === 1 ? 'is' : 'are'} already uploaded and
            needs your input. Open it from your file list to finish.
          </span>
        </Alert>
      ) : null}

      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>
        {completedCount + existingSurveyFiles.length} of {maxFiles} files
        uploaded
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {existingSurveyFiles.map((sf) => (
          <Box
            key={`survey-file-${sf.file_id}`}
            data-testid={`survey-file-tile-${sf.file_id}`}
            data-highlighted={
              sf.file_id === highlightedSurveyFileId ? 'true' : undefined
            }
            onClick={() => onSurveyFileDetail?.(sf)}
            sx={{
              ...tileBase,
              // ENG-4064: the tile this upload matched, so "already on this
              // question" points at something the renter can see.
              ...(sf.file_id === highlightedSurveyFileId
                ? { borderColor: 'primary.main', borderWidth: 2 }
                : {}),
              ...(sf.status !== 1 || sf.is_rejected
                ? {
                    // Deletable: row layout — left zone | divider | content
                    flexDirection: 'row',
                    px: 0,
                    py: 0,
                    alignItems: 'stretch',
                  }
                : {}),
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.light' },
            }}
          >
            {sf.status !== 1 || sf.is_rejected ? (
              <>
                {/* Left delete zone */}
                <Box
                  component="button"
                  data-testid={`remove-survey-file-${sf.file_id}`}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onSurveyFileDelete?.(sf);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    flexShrink: 0,
                    bgcolor: '#F4F6F8',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '11px 0 0 11px',
                    '&:hover': { bgcolor: '#FFE4E4' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Box>

                {/* Vertical divider */}
                <Box sx={{ width: '1px', bgcolor: '#E8ECEF', flexShrink: 0 }} />

                {/* File info */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    minWidth: 0,
                  }}
                >
                  <InsertDriveFileIcon
                    sx={{
                      fontSize: 28,
                      color: sf.is_rejected ? 'warning.dark' : 'success.main',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={filenameSx}>{sf.file_name}</Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: sf.is_rejected ? 600 : 400,
                        color: sf.is_rejected
                          ? 'warning.dark'
                          : 'text.secondary',
                      }}
                    >
                      {sf.is_rejected
                        ? 'Rejected — please re-upload'
                        : 'Tap to view'}
                    </Typography>
                  </Box>
                </Box>

                {/* Right chevron zone — white, no divider */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    flexShrink: 0,
                    borderRadius: '0 11px 11px 0',
                  }}
                >
                  <ChevronRightIcon
                    sx={{ fontSize: 18, color: 'grey.800', flexShrink: 0 }}
                  />
                </Box>
              </>
            ) : (
              <>
                {/* Approved — keep original full-width layout */}
                <Box sx={tileLine1Sx}>
                  <InsertDriveFileIcon
                    sx={{ fontSize: 20, color: 'success.main' }}
                  />
                  <Typography sx={filenameSx}>{sf.file_name}</Typography>
                  <ChevronRightIcon
                    sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }}
                  />
                </Box>
                <Box sx={{ pl: '32px', mt: '-2px', minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, color: 'success.main' }}>
                    Approved
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        ))}
        {Array.from({
          length: Math.max(0, maxFiles - existingSurveyFiles.length),
        }).map((_, index) => renderTile(index))}
      </Box>
    </Box>
  );
}
